import type { Annotation } from "@rapid/types/annotation";
import { Prediction } from "@rapid/utils/api/inference";

export const DEFAULT_CONFIDENCE_THRESHOLD = 0.5;

export const SAM3_INFERENCE_CONFIDENCE_THRESHOLD = 0;

export const predictionToAnnotation = (prediction: Prediction, frameId: string): Annotation => {
    const {
        x,
        y,
        width,
        height,
        detection_id: id,
        class: classLabel,
        thumbnailDataUrl,
        fileId,
        confidence,
        masks,
        type
    } = prediction;
    return {
        id,
        x,
        y,
        width,
        height,
        label: classLabel.replace(/\s*\(\d+%\)$/, ""),
        draft: false,
        thumbnailDataUrl,
        fileId,
        frameId,
        confidence,
        masks,
        type
    };
};

/**
 * OWLv2 confidence floor - predictions below this raw confidence are considered noise.
 * The model rarely produces meaningful results below 0.85 raw confidence.
 */
const OWLVIT_CONFIDENCE_FLOOR = 0.85;

/**
 * OWLv2 confidence range - the meaningful confidence range above the floor.
 * Raw confidence 0.85-1.0 maps to adjusted 0-1.
 */
const OWLVIT_CONFIDENCE_RANGE = 1 - OWLVIT_CONFIDENCE_FLOOR; // 0.15

/**
 * OwlVIT returns a confidence score between 0 and 1, but the model is super sensitive and it usually works great
 * within 0.95 +- 0.02 - so we have this heuristic to adjust the confidence score to a score between 0 and 1.
 *
 * Raw confidence mapping (more aggressive than before):
 * - 0.85 raw -> 0.0 adjusted (floor - anything below is noise)
 * - 0.90 raw -> 0.33 adjusted
 * - 0.95 raw -> 0.67 adjusted
 * - 1.00 raw -> 1.0 adjusted
 *
 * @param confidence - The confidence score from OwlVIT (0-1)
 * @returns The adjusted confidence score (0-1, clamped)
 */
const adjustedConfidenceFromOwlvit = (confidence: number) => {
    // Map raw confidence 0.85-1.0 to adjusted 0-1
    // Anything below 0.85 maps to negative (will be filtered out)
    const adjusted = (confidence - OWLVIT_CONFIDENCE_FLOOR) / OWLVIT_CONFIDENCE_RANGE;
    return Math.max(0, Math.min(1, adjusted));
};

/**
 * Converts adjusted confidence back to raw OWLv2 confidence.
 * @param adjustedConfidence - The adjusted confidence score (0-1)
 * @returns The raw confidence score from OWLv2 (0.85-1.0)
 */
const adjustedConfidenceToOwlvit = (adjustedConfidence: number) => {
    return adjustedConfidence * OWLVIT_CONFIDENCE_RANGE + OWLVIT_CONFIDENCE_FLOOR;
};

export { adjustedConfidenceFromOwlvit, adjustedConfidenceToOwlvit };

/**
 * Box prompt item for OWLv2 inference
 */
export interface BoxPromptItem {
    x: number;
    y: number;
    w: number;
    h: number;
    negative: boolean;
    cls: string;
}

/**
 * Box prompting data for OWLv2 inference
 */
export interface BoxPromptingData {
    file_uuid: string;
    width: number;
    height: number;
    boxes: BoxPromptItem[];
    /** Optional direct image URL - when provided, backend skips storage lookup.
     * Used for improve flow where images are already on CDN (e.g., source.roboflow.one) */
    image_url?: string;
}

/**
 * Options for building box prompting data
 */
export interface BuildBoxPromptingDataOptions {
    /** Filter annotations to only include this class. If not provided, includes all classes. */
    classFilter?: string;
    /** Workspace URL prefix for file_uuid. Defaults to "anonymous". */
    workspaceUrl?: string;
    /** Optional map of frameId to image URL for direct CDN access (skips storage lookup on backend).
     * Used for improve flow where images are already on CDN (e.g., source.roboflow.one) */
    imageUrls?: Map<string, string>;
    /**
     * Maximum number of box prompts to use per class. Defaults to a fixed number.
     * Using too many prompts can lead to more false positives (hallucinations).
     * The highest confidence annotations are selected first.
     */
    maxPromptsPerClass?: number;
}

/** Default maximum number of box prompts per class */
const DEFAULT_MAX_PROMPTS_PER_CLASS = 10;

/**
 * Builds box prompting data from annotations for OWLv2 inference.
 * This is used for "Find Similar Objects" functionality.
 *
 * Limits the number of prompts per class to avoid hallucinations - more prompts
 * can lead to more false positives. Selects highest confidence annotations first.
 *
 * @param annotations - Array of annotations with frameId and fileId
 * @param mediaDimensions - Map of frameId to {width, height} dimensions
 * @param options - Optional configuration for filtering and formatting
 * @returns Array of BoxPromptingData ready for inference
 */
export const buildBoxPromptingData = (
    annotations: (Annotation & { frameId: string; fileId: string })[],
    mediaDimensions: Map<string, { width: number; height: number }>,
    options: BuildBoxPromptingDataOptions = {}
): BoxPromptingData[] => {
    const {
        classFilter,
        workspaceUrl = "anonymous",
        imageUrls,
        maxPromptsPerClass = DEFAULT_MAX_PROMPTS_PER_CLASS
    } = options;

    // Filter to only include confirmed annotations (not drafts or empty)
    let validAnnotations = annotations.filter(
        (anno) => !anno.draft && !anno.empty && anno.width > 0 && anno.height > 0
    );

    // Apply class filter if provided
    if (classFilter) {
        validAnnotations = validAnnotations.filter((anno) => anno.label === classFilter);
    }

    if (validAnnotations.length === 0) {
        return [];
    }

    // Limit to top N highest confidence annotations per class
    // This reduces false positives - more prompts = more hallucinations
    if (maxPromptsPerClass > 0) {
        // Group by class label
        const annotationsByClass = new Map<
            string,
            (Annotation & { frameId: string; fileId: string })[]
        >();
        validAnnotations.forEach((anno) => {
            const label = anno.label || "unknown";
            if (!annotationsByClass.has(label)) {
                annotationsByClass.set(label, []);
            }
            annotationsByClass.get(label)!.push(anno);
        });

        // For each class, sort by confidence and take top N
        validAnnotations = [];
        annotationsByClass.forEach((classAnnotations) => {
            // Sort by confidence descending (highest first)
            const sorted = [...classAnnotations].sort(
                (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0)
            );
            // Take top N highest confidence annotations
            validAnnotations.push(...sorted.slice(0, maxPromptsPerClass));
        });
    }

    // Group annotations by frameId
    const annotationsByFrame = new Map<
        string,
        (Annotation & { frameId: string; fileId: string })[]
    >();
    validAnnotations.forEach((anno) => {
        const frameId = anno.frameId;
        if (!annotationsByFrame.has(frameId)) {
            annotationsByFrame.set(frameId, []);
        }
        annotationsByFrame.get(frameId)!.push(anno);
    });

    // Build prompting data for each frame
    const boxPromptingData: BoxPromptingData[] = [];

    annotationsByFrame.forEach((frameAnnotations, frameId) => {
        // Get dimensions for this frame
        const dimensions = mediaDimensions.get(frameId);
        if (!dimensions) {
            console.warn(`[buildBoxPromptingData] No dimensions found for frame ${frameId}`);
            return;
        }

        const data: BoxPromptingData = {
            file_uuid: `${workspaceUrl}/${frameId}`,
            width: dimensions.width,
            height: dimensions.height,
            boxes: frameAnnotations.map((anno) => ({
                x: Math.round(anno.x),
                y: Math.round(anno.y),
                w: Math.round(anno.width),
                h: Math.round(anno.height),
                negative: false,
                cls: anno.label
            }))
        };

        // If image URL is provided for this frame, include it (skips storage lookup on backend)
        const imageUrl = imageUrls?.get(frameId);
        if (imageUrl) {
            data.image_url = imageUrl;
        }

        boxPromptingData.push(data);
    });

    return boxPromptingData;
};

/**
 * Stratified confidence thresholds for box prompting based on prompt count.
 *
 * These values are in the ADJUSTED confidence scale (0-1 after adjustedConfidenceFromOwlvit).
 * The mapping to raw OWLv2 confidence is:
 *   adjusted = (raw - 0.85) / 0.15
 *   raw = adjusted * 0.15 + 0.85
 *
 * Stratified thresholds (adjusted -> raw):
 * - 1 prompt:   0.45 adjusted = 0.918 raw  (most permissive - user drew one box, trust it more)
 * - 2 prompts:  0.55 adjusted = 0.933 raw
 * - 3 prompts:  0.65 adjusted = 0.948 raw
 * - 4 prompts:  0.72 adjusted = 0.958 raw
 * - 5 prompts:  0.78 adjusted = 0.967 raw
 * - 6-9 prompts: 0.82 adjusted = 0.973 raw
 * - 10+ prompts: 0.88 adjusted = 0.982 raw  (most strict - many prompts = more hallucination risk)
 *
 * Note: These are slightly more permissive than before to allow finding more objects,
 * with the fallback mechanism still available for progressive discovery.
 */
const BOX_PROMPT_CONFIDENCE_THRESHOLDS: { maxPrompts: number; threshold: number }[] = [
    { maxPrompts: 1, threshold: 0.45 }, // 0.918 raw
    { maxPrompts: 2, threshold: 0.55 }, // 0.933 raw
    { maxPrompts: 3, threshold: 0.65 }, // 0.948 raw
    { maxPrompts: 4, threshold: 0.72 }, // 0.958 raw
    { maxPrompts: 5, threshold: 0.78 }, // 0.967 raw
    { maxPrompts: 9, threshold: 0.82 }, // 0.973 raw
    { maxPrompts: Infinity, threshold: 0.88 } // 0.982 raw (10+ prompts)
];

/**
 * Calculates a dynamic confidence threshold based on the number of prompts.
 * More prompts = higher threshold (model gets more "creative"/hallucinates with more examples).
 *
 * Uses a stratified approach with discrete thresholds for 1, 2, 3, 4, 5, 6-9, and 10+ prompts.
 * This provides fine-grained control at low prompt counts where sensitivity matters most.
 *
 * Note: These thresholds are in the ADJUSTED confidence scale (0-1 after adjustedConfidenceFromOwlvit).
 * For box prompting, we use higher thresholds than text prompting because visual prompts
 * tend to produce more false positives.
 *
 * @param promptCount - Number of box prompts being used
 * @returns Confidence threshold value in adjusted scale (0-1)
 */
export const calculateBoxPromptConfidenceThreshold = (promptCount: number): number => {
    // Find the first threshold where promptCount <= maxPrompts
    for (const { maxPrompts, threshold } of BOX_PROMPT_CONFIDENCE_THRESHOLDS) {
        if (promptCount <= maxPrompts) {
            return threshold;
        }
    }
    // Fallback (should never reach here due to Infinity)
    return BOX_PROMPT_CONFIDENCE_THRESHOLDS[BOX_PROMPT_CONFIDENCE_THRESHOLDS.length - 1].threshold;
};
